import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query from JavaScript.
 *
 * The header needs its breakpoint as a value, not only as a Tailwind
 * class: "what is inline" and "what is under Mere ▾" is one split of
 * one array, and NavOverflow derives its active-underline and its
 * summed badge from the slice it was handed. Hiding the promoted links
 * with a CSS class would leave them counted in the menu while they are
 * also visible in the row.
 *
 * ``matchMedia`` is absent outside a browser, in which case this
 * reports ``false`` — the narrow layout, which is the one that always
 * fits.
 */
export function useMediaQuery(query: string): boolean {
  const read = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState<boolean>(read);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }
    const mql = window.matchMedia(query);
    // Re-read on mount: the query can already have changed between the
    // first render and this effect (a resize, or a restored window).
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}
